<script setup lang="ts">
import { Ban, CircleCheck, Eye, Hand, Loader2, Pencil } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast/use-toast';
import {
    listConnectorTools,
    setConnectorToolDecision,
    ToolDecision,
    type ToolPolicy
} from '@/services/mcpConnectors';

const props = defineProps<{
    tenantId: string;
    provider: string;
}>();

const toast = useToast();

const policies = ref<ToolPolicy[]>([]);
const isLoading = ref(true);
/** Tool names with a write in flight, so their row disables without freezing the list. */
const saving = ref<Set<string>>(new Set());

const groups = computed(() => [
    {
        label: 'Read-only tools',
        icon: Eye,
        tools: policies.value.filter((policy) => policy.read_only)
    },
    {
        label: 'Write & delete tools',
        icon: Pencil,
        tools: policies.value.filter((policy) => !policy.read_only)
    }
]);

// No group-level shortcut on purpose: granting a dozen write tools in one click is
// exactly what this screen should make deliberate.
const CHOICES = [
    {
        value: ToolDecision.ALLOW,
        icon: CircleCheck,
        label: 'Run without asking'
    },
    { value: ToolDecision.ASK, icon: Hand, label: 'Ask every time' },
    { value: ToolDecision.DENY, icon: Ban, label: 'Never available' }
] as const;

async function load() {
    isLoading.value = true;
    try {
        policies.value = await listConnectorTools(
            props.tenantId,
            props.provider
        );
    } catch (error) {
        toast.toast({
            title: 'Failed to load tool permissions',
            description: 'Please close and reopen this connector.',
            variant: 'destructive'
        });
        console.error(error);
    } finally {
        isLoading.value = false;
    }
}

/**
 * Applied locally first so the list does not flicker, then rolled back if the server
 * refuses — a row that looks changed but was not stored governs a real call.
 */
async function decide(tool: ToolPolicy, decision: ToolDecision) {
    if (tool.decision === decision || saving.value.has(tool.name)) return;

    const previous = tool.decision;
    tool.decision = decision;
    saving.value = new Set([...saving.value, tool.name]);

    try {
        await setConnectorToolDecision(
            props.tenantId,
            props.provider,
            tool.name,
            decision
        );
    } catch (error) {
        tool.decision = previous;
        toast.toast({
            title: 'Permission not saved',
            description: 'Your change was undone. Please try again.',
            variant: 'destructive'
        });
        console.error(error);
    } finally {
        const pending = new Set(saving.value);
        pending.delete(tool.name);
        saving.value = pending;
    }
}

watch(() => [props.tenantId, props.provider], load, { immediate: true });
</script>

<template>
    <div class="mcp-tool-permissions">
        <p class="text-xs text-muted-foreground">
            Choose when Elqano may use each tool. Removing your access to this
            connector forgets these choices.
        </p>

        <div v-if="isLoading" class="mt-4 text-sm text-muted-foreground">
            <Loader2 class="h-4 w-4 animate-spin" />
        </div>

        <template v-else>
            <section
                v-for="group in groups"
                :key="group.label"
                class="mt-4 first:mt-5"
            >
                <!-- The two groups differ in kind, not in degree: one can only read.
                     Icon and weight carry that; a filled band would only push the
                     header out of line with the rows under it. -->
                <h5
                    class="flex items-center gap-2 border-b pb-2 text-xs font-semibold"
                >
                    <component :is="group.icon" class="h-3.5 w-3.5" />
                    {{ group.label }}
                    <span class="font-normal text-muted-foreground">
                        {{ group.tools.length }}
                    </span>
                </h5>

                <div
                    v-for="tool in group.tools"
                    :key="tool.name"
                    class="flex items-center justify-between gap-4 border-b py-2 last:border-b-0"
                >
                    <span class="min-w-0 truncate font-mono text-xs">
                        {{ tool.name }}
                    </span>
                    <!-- Icon only, and no colour: three states of one setting, not
                         three warnings. The chosen one is the raised chip. -->
                    <div
                        class="flex shrink-0 items-center gap-0.5 rounded-md bg-muted/50 p-0.5"
                    >
                        <Button
                            v-for="choice in CHOICES"
                            :key="choice.value"
                            size="icon-sm"
                            variant="ghost"
                            :title="choice.label"
                            :aria-label="choice.label"
                            :aria-pressed="tool.decision === choice.value"
                            :class="
                                tool.decision === choice.value
                                    ? 'bg-background border shadow-sm hover:bg-background'
                                    : 'text-muted-foreground/60'
                            "
                            :disabled="saving.has(tool.name)"
                            @click="decide(tool, choice.value)"
                        >
                            <component :is="choice.icon" class="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </section>
        </template>
    </div>
</template>
